import { Confirm, useNotify } from "react-admin";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Button,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import { useCallback, useEffect, useRef, useState } from "react";
import { contextApi } from "../../providers/env";
import FileDownload from "@mui/icons-material/FileOpenOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { motion, AnimatePresence } from "framer-motion";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Params, useParams } from "react-router";
import { useCustomPermissions } from '../../hooks/usePermissions';
import { PermissionGuard } from '../../components/PermissionGuard';

type UploadedFile = {
  id: string;
  name: string;
  fileExt: string;
  size: number;
  updatedAt: Date;
  originFile: File;
};

function FilesTable({
  files,
  isLoadingDeleteTrainBot,
  handleRemoveFile,
  onPreview,
}: any) {
  const { hasPermission } = useCustomPermissions();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        marginTop: "1rem",
      }}
    >
      <AnimatePresence>
        {files.map((file: any, index: number) => (
          <Box
            onClick={(e) => {
              e.stopPropagation();
            }}
            key={file.id}
            component={motion.div}
            layout
            initial={{ opacity: 0, y: -20, scale: 0.995 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.995 }}
            transition={{ type: "spring", stiffness: 400, damping: 28 }}
            sx={{
              background: "#e2e2e2",
              borderRadius: ".5rem",
              padding: ".5625em",
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              transformStyle: "preserve-3d",
              WebkitTransformStyle: "preserve-3d",
              willChange: "transform, opacity",
              gap: "8px",
            }}
          >
            {hasPermission('context', 'delete') && (
              <Tooltip title="Delete file">
                <span>
                  <IconButton
                    size="small"
                    sx={{ width: "50px", height: "50px" }}
                    disabled={isLoadingDeleteTrainBot}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      handleRemoveFile(index, file.id);
                    }}
                  >
                    <DeleteIcon
                      sx={{
                        background: "#fff",
                        border: "1px solid #dc3545",
                        color: "#dc3545 ",
                        transition: "all 0.3s ease",
                        transform: "translate3d(0px, 0px, 0px) scale3d(1, 1, 1)",
                        fontSize: "16px",
                        padding: "10px",
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        opacity: 1,
                        "&:hover": {
                          backgroundColor: "#dc3545",
                          color: "#fff",
                          boxShadow: "0 0 0 .125em hsla(0, 0%, 100%, .9)",
                        },
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ textAlign: "start" }}>
                <Typography variant="body1">{file.name}</Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: ".625em",
                    opacity: 0.5,
                    transition: "opacity .25s ease-in-out",
                    whiteSpace: "nowrap",
                    "&:hover": { opacity: 1 },
                  }}
                >
                  {file.size > 0 ? `${(file.size / 1024).toFixed(2)} KB` : "-"}
                </Typography>
              </Box>
              <Tooltip title="Preview file">
                <span>
                  <IconButton
                    sx={{ width: "50px", height: "50px" }}
                    size="small"
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      onPreview(file);
                    }}
                  >
                    <VisibilityIcon
                      sx={{
                        background: "#fff",
                        border: "1px solid #7539FF",
                        color: "#7539FF ",
                        transition: "all 0.3s ease",
                        transform:
                          "translate3d(0px, 0px, 0px) scale3d(1, 1, 1)",
                        fontSize: "16px",
                        padding: "10px",
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        opacity: 1,
                        "&:hover": {
                          backgroundColor: "#7539FF",
                          color: "#fff",
                          boxShadow: "0 0 0 .125em hsla(0, 0%, 100%, .9)",
                        },
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </AnimatePresence>
    </Box>
  );
}

const CustomDeleteWithConfirm = ({ handleConfirm, isLoading }: any) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<DeleteIcon sx={{ color: "inherit" }} />}
        onClick={() => setOpen(true)}
        disabled={isLoading}
        sx={{
          borderColor: "#dc3545",
          color: "#dc3545",
          boxShadow: "inset 0 0 0 0 #fff",
          transition: "all 0.5s",
          "&:hover": {
            backgroundColor: "#dc3545",
            borderColor: "#dc3545",
            color: "#fff",
            boxShadow: "inset 0 50px 0 0 #dc3545",
          },
        }}
      >
        {isLoading ? "Deleting..." : "Delete"}
      </Button>

      <Confirm
        isOpen={open}
        loading={isLoading}
        title="Are you sure?"
        content="You won't be able to revert this!"
        onConfirm={handleConfirm}
        onClose={() => setOpen(false)}
      />
    </>
  );
};
function UploadedFilesTable({
  files,
  isLoadingDeleteTrainBot,
  handleDeleteFile,
  handleDownloadFile,
  isLoadingDownloadTrainBot,
  onPreview,
}: any) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <AnimatePresence>
        {files?.map((file: any, index: number) => (
          <Box
            onClick={(e) => {
              e.stopPropagation();
            }}
            key={file.id}
            component={motion.div}
            layout
            initial={{ opacity: 0, y: -20, scale: 0.995 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -12, scale: 0.995 }}
            transition={{ type: "spring", stiffness: 400, damping: 28 }}
            sx={{
              background: "#fff",
              borderRadius: ".5rem",
              padding: "1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              transformStyle: "preserve-3d",
              WebkitTransformStyle: "preserve-3d",
              willChange: "transform, opacity",
              gap: "8px",
              border: "1px solid #dee2e6",
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ textAlign: "start" }}>
                <Typography variant="body1">{file.filename}</Typography>
              </Box>
              <Box sx={{ display: "flex", gap: "8px" }}>
                <PermissionGuard resource="context" action="view">
                  <Button
                    variant="outlined"
                    onClick={() =>
                      handleDownloadFile(file.id.toString(), file.filename)
                    }
                    onLoad={isLoadingDownloadTrainBot}
                    sx={{
                      borderColor: "#7539FF",
                      color: "#7539FF",
                      boxShadow: "inset 0 0 0 0 #fff",
                      transition: "all 0.5s",

                      "&:hover": {
                        backgroundColor: "#7539FF",
                        borderColor: "#7539FF",
                        color: "#fff",
                        boxShadow: "inset 0 50px 0 0 #7539FF",
                      },
                    }}
                    startIcon={<DownloadIcon sx={{ color: "inherit" }} />}
                  >
                    Download
                  </Button>
                </PermissionGuard>

                <PermissionGuard resource="context" action="delete">
                  <CustomDeleteWithConfirm
                    handleConfirm={() => handleDeleteFile(file.id.toString())}
                    isLoading={isLoadingDeleteTrainBot}
                  />
                </PermissionGuard>
              </Box>
            </Box>
          </Box>
        ))}
      </AnimatePresence>
    </Box>
  );
}

export const Context = (props: any) => {
  const { id } = useParams<{ id: string }>();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const notify = useNotify();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDeleteTrainBot, setisLoadingDeleteTrainBot] = useState(false);
  const [isLoadingDownloadTrainBot, setisLoadingDownloadTrainBot] =
    useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [fileContent, setFileContent] = useState("");
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<string | null>(null);
  const [previewName, setPreviewName] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const params = useParams<Params>();
  const agentIdFromParams = params?.agentId;
  const agentId =
    props?.agentId ??
    agentIdFromParams ??
    (() => {
      try {
        return localStorage.getItem("agentId");
      } catch {
        return undefined;
      }
    })();
  // helper: only accept .txt
  const isTxtFile = (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    return ext === "txt" || file.type.startsWith("text");
  };

  // add files from FileList - create UploadedFile entries
  const addFilesFromList = (fileList: FileList | null) => {
    if (!fileList) return;
    const arr = Array.from(fileList);
    const newFiles: UploadedFile[] = [];

    for (const file of arr) {
      if (!isTxtFile(file)) {
        notify(`${file.name} is not a .txt file — skipped.`, {
          type: "warning",
        });
        continue;
      }

      if (file.size === 0) {
        notify("File is empty (0 KB). Please choose another file.", {
          type: "warning",
        });
        continue;
      }

      if (file.size < 1024) {
        notify("File is smaller than 1 KB — please verify.", {
          type: "warning",
        });
      }

      const fileNameNoExt =
        file.name.lastIndexOf(".") > 0
          ? file.name.substring(0, file.name.lastIndexOf("."))
          : file.name;

      newFiles.push({
        id: `${file.name}-${file.size}-${Date.now()}`,
        name: fileNameNoExt,
        fileExt: file.name.split(".").pop()?.toLowerCase() || "",
        size: file.size,
        updatedAt: new Date(),
        originFile: file,
      });
    }

    if (newFiles.length > 0) {
      setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }
  };

  // --- drag & drop handlers ---
  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };
  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // only reset when leaving the drop zone
    setIsDragging(false);
  };
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dtFiles = e.dataTransfer.files;
    addFilesFromList(dtFiles);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    addFilesFromList(selectedFiles);
    e.currentTarget.value = "";
  };

  const handleUpload = async () => {
    try {
      if (files.length === 0) {
        notify("No files selected", { type: "warning" });
        return;
      }

      setIsLoading(true);
      setProgress(0);
      let progressValue = 0;

      const fastStep = 2;
      const slowStep = 1;
      const fastLimit = 50;
      const maxProgressBeforeApi = 99;

      const interval = setInterval(() => {
        if (progressValue < fastLimit) {
          progressValue += fastStep;
        } else if (progressValue < maxProgressBeforeApi) {
          progressValue += slowStep;
        }

        if (progressValue >= maxProgressBeforeApi) {
          progressValue = maxProgressBeforeApi;
        }

        setProgress(progressValue);
      }, 100);

      try {
        const nativeFiles = files.map((f) => f.originFile);
        await contextApi.uploadContextTxt({ files: nativeFiles, agentId });
        clearInterval(interval);
        setProgress(100);
        notify("Uploaded successfully!", { type: "success" });
        setFiles([]);
        fetchConfig();
      } catch (err: any) {
        clearInterval(interval);
        console.error("Upload failed:", err);
        notify(err?.response?.data?.detail || "Upload failed", {
          type: "error",
        });
        setProgress(0);
      }
    } finally {
      setIsLoading(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  const handleDownloadConsolidated = useCallback(() => {
    contextApi
      .downloadAllContexts({ agentId })
      .then((response) => {
        const blob = new Blob([response.data], { type: "text/plain" });
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = "ConsolidatedFiles.txt";
        document.body.appendChild(a);
        a.click();
        a.remove();

        window.URL.revokeObjectURL(url);

        notify("The file has been downloaded", { type: "success" });
      })
      .catch((error) => {
        console.error("File download failed:", error);
        notify(
          error?.response?.data?.detail ||
            "File download failed, please try again",
          { type: "error" }
        );
      });
  }, [notify]);

  const handleDeleteFile = async (id: string) => {
    try {
      setisLoadingDeleteTrainBot(true);
      await contextApi.deleteContext({ fileId: id });

      notify("Deleted successfully!", { type: "success" });
      setFiles([]);
      setisLoadingDeleteTrainBot(false);
      fetchConfig();
    } catch (err: any) {
      console.error("Delete failed:", err);
      notify(err?.response?.data?.detail || "Delete failed", { type: "error" });
      setisLoadingDeleteTrainBot(false);
    }
  };
  const handleDownloadFile = useCallback(
    (id: string, name: string) => {
      contextApi
        .downloadFile({ fileId: id })
        .then((response) => {
          const blob = new Blob([response.data], { type: "text/plain" });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `${name}.txt`;
          document.body.appendChild(a);
          a.click();
          a.remove();

          window.URL.revokeObjectURL(url);

          notify("The file has been downloaded", { type: "success" });
        })
        .catch((error) => {
          console.error("File download failed:", error);
          notify(
            error?.response?.data?.detail ||
              "File download failed, please try again",
            { type: "error" }
          );
        });
    },
    [notify]
  );

  const handleRemoveFile = (index: number, id: string | undefined) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePreview = async (file: UploadedFile) => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewText(null);
    setPreviewType(null);
    setPreviewName(file.name);

    const f = file.originFile;
    const mime = f.type || (file.fileExt ? `application/${file.fileExt}` : "");
    setPreviewType(mime);

    if (mime.startsWith("text/") || file.fileExt === "txt") {
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewText(String(reader.result ?? ""));
        setPreviewOpen(true);
      };
      reader.readAsText(f);
      return;
    }

    try {
      const url = URL.createObjectURL(f);
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (err) {
      notify("Cannot preview this file type.", { type: "error" });
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setPreviewText(null);
    setPreviewType(null);
    setPreviewName(null);
  };

  const handleClose = () => setOpen(false);

  const fetchConfig = async () => {
    try {
      const res = await contextApi.listContextTxt({ agentId });
      setData(res.data ?? []);
    } catch (err) {
      console.error("fetchConfig error:", err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchConfig();
  }, []);

  return (
    <>
      <Card sx={{ margin: 2 }}>
        <CardHeader title={`Upload Context Files`} />
        <CardContent>
          <Box
            sx={{
              marginBottom: 2,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body1" sx={{ marginTop: 1 }}>
              Upload your context file (.TXT ONLY)
            </Typography>
          </Box>

          {isLoading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={progress} />
              <Typography variant="caption">{progress}%</Typography>
              <Typography
                variant="body1"
                sx={{ marginTop: 1, marginBottom: 1, fontWeight: 600 }}
              >
                The upload process may take a few minutes. Please wait.
              </Typography>
            </Box>
          )}

          <input
            type="file"
            accept=".txt"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            multiple
          />

          <Box
            component={motion.div}
            layout
            transition={{ duration: 0.35, ease: "easeOut" }}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={onDragOver}
            onDragEnter={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                fileInputRef.current?.click();
              }
            }}
            sx={{
              border: "2px dashed #ccc",
              padding: "2rem",
              textAlign: "center",
              cursor: "pointer",
              borderRadius: "8px",
              "&:hover": {
                backgroundColor: "#f9f9f9",
              },
              transformStyle: "preserve-3d",
              WebkitTransformStyle: "preserve-3d",
              overflow: "hidden",
              backgroundColor: isDragging ? "action.hover" : "transparent",
            }}
          >
            <FileDownload fontSize="large" sx={{ mr: 0.5, color: "#7539FF" }} />

            <Typography variant="body1" sx={{ marginTop: 1 }}>
              {isDragging ? "Drop files here" : "Drop your files here or "}
              <span style={{ color: "#7539FF" }}>browse</span>
            </Typography>

            {files && files.length > 0 ? (
              <>
                <FilesTable
                  files={files}
                  onPreview={handlePreview}
                  handleRemoveFile={handleRemoveFile}
                  isLoadingDeleteTrainBot={isLoadingDeleteTrainBot}
                />
                <Dialog
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  open={previewOpen}
                  onClose={handleClosePreview}
                  fullWidth
                  maxWidth="md"
                >
                  <DialogTitle>{previewName || "Preview"}</DialogTitle>
                  <DialogContent dividers>
                    {previewText ? (
                      <Box
                        component="pre"
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          maxHeight: "60vh",
                          overflow: "auto",
                          fontSize: "0.875rem",
                        }}
                      >
                        {previewText}
                      </Box>
                    ) : previewUrl ? (
                      previewType?.startsWith("image/") ? (
                        <img
                          src={previewUrl}
                          alt={previewName ?? "preview"}
                          style={{ maxWidth: "100%" }}
                        />
                      ) : previewType === "application/pdf" ||
                        previewName?.endsWith(".pdf") ? (
                        <iframe
                          src={previewUrl}
                          title={previewName ?? "pdf-preview"}
                          style={{
                            width: "100%",
                            height: "70vh",
                            border: "none",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          <Typography>No data</Typography>
                          <Button
                            variant="contained"
                            onClick={() => {
                              if (!previewUrl) return;
                              window.open(previewUrl, "_blank");
                            }}
                          >
                            Open in new tab
                          </Button>
                        </Box>
                      )
                    ) : (
                      <Typography>Preparing preview...</Typography>
                    )}
                  </DialogContent>
                </Dialog>
              </>
            ) : null}
          </Box>

          <PermissionGuard resource="context" action="create">
            <Button
              onClick={handleUpload}
              sx={{
                marginTop: "16px",
                backgroundColor: "#7539FF",
                border: "1px solid #7539FF",
                boxShadow: "inset 0 0 0 0 #fff",
                borderRadius: "4px",
                transition: "all 0.5s",
                color: "#000",
                "&:hover": {
                  backgroundColor: "#fff",
                  borderColor: "#7539FF",
                  color: "#7539FF",
                  boxShadow: "inset 0 50px 0 0 #fff",
                },
              }}
              disabled={isLoading || files.length === 0}
            >
              {isLoading ? "Uploading..." : "Upload Files"}
            </Button>
          </PermissionGuard>
        </CardContent>
      </Card>

      <PermissionGuard resource="context" action="view">
        <Card sx={{ margin: 2 }}>
          <CardHeader title={`Download Consolidated File`} />

          <CardContent>
            <Box>
              <Button
                onClick={() => handleDownloadConsolidated()}
                sx={{
                  marginTop: "16px",
                  backgroundColor: "#7539FF",
                  border: "1px solid #7539FF",
                  boxShadow: "inset 0 0 0 0 #fff",
                  borderRadius: "4px",
                  transition: "all 0.5s",
                  color: "#000",
                  "&:hover": {
                    backgroundColor: "#fff",
                    borderColor: "#7539FF",
                    color: "#7539FF",
                    boxShadow: "inset 0 50px 0 0 #fff",
                  },
                }}
              >
                Download Consolidated File
              </Button>
            </Box>
          </CardContent>
        </Card>
      </PermissionGuard>

      <Card sx={{ margin: 2 }}>
        <CardHeader title={`Uploaded Files`} />

        <CardContent>
          <UploadedFilesTable
            files={data}
            isLoadingDownloadTrainBot={isLoadingDownloadTrainBot}
            isLoadingDeleteTrainBot={isLoadingDeleteTrainBot}
            handleDeleteFile={handleDeleteFile}
            handleDownloadFile={handleDownloadFile}
          />
        </CardContent>
      </Card>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>Preview file content</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              maxHeight: "60vh",
              overflow: "auto",
            }}
          >
            {fileContent}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};
